import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "../firebase";
import "./ProductDeepLink.css";

const PRODUCT_COLLECTIONS = [
  "allproducts",
  "nameprinted",
  "photoprinted",
];

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.liyagroup.pitics";

const ProductDeepLink = () => {
  const { productId: routeProductId } = useParams();

  const productId =
    routeProductId?.trim() ||
    window.location.pathname.split("/").filter(Boolean)[1]?.trim() ||
    "";

  const [product, setProduct] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      if (!productId) {
        setLoading(false);
        setError("Invalid PITICS product link.");
        return;
      }

      try {
        setLoading(true);
        setError("");

        let found = null;

        for (const collectionName of PRODUCT_COLLECTIONS) {
          const productQuery = query(
            collection(db, collectionName),
            where("productId", "==", productId),
            limit(1)
          );

          const snapshot = await getDocs(productQuery);

          if (!snapshot.empty) {
            found = {
              ...snapshot.docs[0].data(),
              _collectionName: collectionName,
              _docId: snapshot.docs[0].id,
            };
            break;
          }
        }

        if (cancelled) return;

        if (!found || found.active === false) {
          setProduct(null);
          setLoading(false);
          setError("This product is no longer available.");
          return;
        }

        setProduct(found);

        const imagePath =
          found.productImagePath ||
          (Array.isArray(found.imageMeta)
            ? found.imageMeta.find((item) => item?.path)?.path
            : "") ||
          found.productImageUrl ||
          found.imageUrl ||
          (typeof found.productImage === "string" ? found.productImage : "");

        if (imagePath) {
          try {
            const url = /^https?:\/\//i.test(imagePath)
              ? imagePath
              : await getDownloadURL(ref(storage, imagePath));

            if (!cancelled) {
              setImageUrl(url);
            }
          } catch (imageError) {
            console.warn("PITICS product image could not be loaded:", imageError);
          }
        }

        if (!cancelled) {
          setLoading(false);
        }
      } catch (loadError) {
        console.error("PITICS product deep-link error:", loadError);

        if (!cancelled) {
          setLoading(false);
          setError(
            "Unable to load this product right now. Please try again."
          );
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const getShareDetails = () => {
    const canonicalUrl = `https://pitics.com/p/${encodeURIComponent(productId)}`;
    const price = Number(product?.sellingPrice ?? product?.price);
    const formattedPrice = Number.isFinite(price) && price > 0
      ? new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 0,
        }).format(price)
      : "";
    const title = product?.name || "PITICS Product";
    const text = [
      `${title}${formattedPrice ? ` - ${formattedPrice}` : ""}`,
      "Discover this product on PITICS.",
    ].join("\n");

    return { canonicalUrl, title, text, message: `${text}\n${canonicalUrl}` };
  };

  const shareProduct = async () => {
    if (!productId || !product) return;

    const { canonicalUrl, title, text, message } = getShareDetails();

    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        let shareData = { title, text, url: canonicalUrl };

        // Attach the product image only when the browser and share target support files.
        // A failed image fetch must never prevent sharing the product link.
        if (
          imageUrl &&
          typeof navigator.canShare === "function" &&
          typeof window.File === "function"
        ) {
          try {
            const response = await fetch(imageUrl);
            if (response.ok) {
              const blob = await response.blob();
              const mimeType = blob.type || "image/jpeg";
              const extension = (mimeType.split("/")[1] || "jpg").replace("jpeg", "jpg");
              const imageFile = new File(
                [blob],
                `pitics-product.${extension}`,
                { type: mimeType }
              );
              const fileShareData = {
                title,
                text: `${text}\n${canonicalUrl}`,
                url: canonicalUrl,
                files: [imageFile],
              };
              if (navigator.canShare(fileShareData)) shareData = fileShareData;
            }
          } catch (imageShareError) {
            // Continue with text and link sharing when image sharing is unavailable.
          }
        }

        await navigator.share(shareData);
        return;
      } catch (shareError) {
        // A user-cancelled share is intentional; do not open another share window.
        if (shareError?.name === "AbortError") return;
        console.warn("PITICS native product sharing failed:", shareError);
      }
    }

    // Desktop browsers without Web Share API fall back to WhatsApp Web.
    window.open(
      `https://wa.me/?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareOnWhatsApp = () => {
    if (!productId || !product) return;
    const { message } = getShareDetails();
    window.open(
      `https://wa.me/?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const copyProductLink = async () => {
    if (!productId || !product) return;
    const { canonicalUrl } = getShareDetails();

    let textArea = null;
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(canonicalUrl);
      } else {
        // Legacy fallback for browsers where the Clipboard API is unavailable.
        textArea = document.createElement("textarea");
        textArea.value = canonicalUrl;
        textArea.setAttribute("readonly", "");
        textArea.setAttribute("aria-hidden", "true");
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, textArea.value.length);
        const copied = document.execCommand("copy");
        if (!copied) throw new Error("Clipboard copy is unavailable");
      }
      window.alert("Product link copied. You can share it with anyone.");
    } catch (copyError) {
      console.error("Could not copy PITICS product link:", copyError);
      window.prompt("Copy this PITICS product link:", canonicalUrl);
    } finally {
      if (textArea?.parentNode) textArea.parentNode.removeChild(textArea);
    }
  };

  const openApp = () => {
    // The same verified HTTPS App Link is intentionally used.
    // Android can open PITICS directly when the app is installed.
    window.location.href =
      `https://pitics.com/p/${encodeURIComponent(productId)}`;
  };

  if (loading) {
    return (
      <div className="pitics-product-page">
        <div className="pitics-product-state">
          <img src="/logo.png" alt="PITICS" className="pitics-state-logo" />
          <div className="pitics-spinner" />
          <h2>Loading product...</h2>
          <p>Please wait.</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pitics-product-page">
        <div className="pitics-product-state">
          <img src="/logo.png" alt="PITICS" className="pitics-state-logo" />
          <h1>Product unavailable</h1>
          <p>{error || "This product could not be found."}</p>

          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pitics-primary-button"
          >
            Download PITICS App
          </a>
        </div>
      </div>
    );
  }

  const sellingPrice = Number(product.sellingPrice ?? product.price);
  const mrp = Number(product.mrp);

  const hasSellingPrice = Number.isFinite(sellingPrice) && sellingPrice > 0;
  const hasMrp = Number.isFinite(mrp) && mrp > 0;
  const hasDiscount =
    hasSellingPrice && hasMrp && mrp > sellingPrice;

  const priceText = hasSellingPrice
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(sellingPrice)
    : "";

  const mrpText = hasMrp
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(mrp)
    : "";

  return (
    <div className="pitics-product-page">
      <header className="pitics-product-header">
        <img src="/logo.png" alt="PITICS" className="pitics-logo" />

        <div className="pitics-header-actions">
          <button type="button" className="pitics-header-button" onClick={shareProduct}>
            Share Product
          </button>
          <button type="button" className="pitics-header-button" onClick={openApp}>
            Open in App
          </button>
        </div>
      </header>

      <main className="pitics-product-card">
        <section className="pitics-image-section">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name || "PITICS product"}
              className="pitics-product-image"
            />
          ) : (
            <div className="pitics-image-placeholder">
              <img src="/logo.png" alt="PITICS" />
            </div>
          )}
        </section>

        <section className="pitics-product-details">
          <div className="pitics-brand">PITICS</div>

          <h1>{product.name || "PITICS Product"}</h1>

          {product.sellerName && (
            <p className="pitics-seller">
              Sold by <strong>{product.sellerName}</strong>
            </p>
          )}

          <div className="pitics-price-row">
            {priceText && (
              <span className="pitics-selling-price">{priceText}</span>
            )}

            {hasDiscount && (
              <span className="pitics-mrp">{mrpText}</span>
            )}
          </div>

          {hasDiscount && (
            <span className="pitics-offer">Special price</span>
          )}

          {product.description && (
            <div className="pitics-description">
              <h2>About this product</h2>
              <p>{product.description}</p>
            </div>
          )}

          <div className="pitics-share-actions">
            <button type="button" className="pitics-primary-button pitics-share-main" onClick={shareProduct}>
              Share Product
            </button>
            <button type="button" className="pitics-secondary-button" onClick={shareOnWhatsApp}>
              Share on WhatsApp
            </button>
            <button type="button" className="pitics-secondary-button" onClick={copyProductLink}>
              Copy Product Link
            </button>
          </div>

          <button
            type="button"
            className="pitics-primary-button pitics-open-app-button"
            onClick={openApp}
          >
            Open in PITICS App
          </button>

          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pitics-play-link"
          >
            Don't have PITICS? Download from Google Play
          </a>

          <div className="pitics-product-id">
            Product ID: {product.productId || productId}
          </div>
        </section>
      </main>

      <footer className="pitics-product-footer">
        PITICS · Personal Informative Text Included For Communication On Stickers
      </footer>
    </div>
  );
};

export default ProductDeepLink;
